/**
 * AppTile — one catalog/connected tile (extracted from IntegrationsPanel
 * 2026-08-18, behavior preserved).
 *
 * Chip rows are view-specific (two-set model): the organization row carries
 * folder-scope chip / account count / shared-by-a-member note / + add
 * account; the personal row carries the share control. The access chip is a
 * render prop — its popover state lives in the panel. Footer: honest
 * auth-method badge, tools count, Connect / ● Connected + ✕.
 */

import React from 'react';
import {
  isStorageToolkit,
  type ComposioToolkit,
  type ConnectedAccount,
  type StorageScope,
} from '../ComposioClient';
import {
  connectLaneFor,
  laneBadgeLabel,
  laneExplainer,
} from '../connectLane';
import panelTheme from '../panelTheme';
import IntegrationIcon from '../IntegrationIcon';
import { useBrowserSignInPhase } from '../useBrowserSignInPhase';

export interface AppTileEntry extends ConnectedAccount {
  sharedWithOrg?: boolean;
  viaShared?: boolean;
  /** Org view: the sharing member's display NAME (never a uid). */
  sharedByName?: string;
}

export interface AppTileProps {
  toolkit: ComposioToolkit;
  connected: boolean;
  connecting: boolean;
  accountsLoading: boolean;
  activeView: 'personal' | 'organization';
  canManage: boolean;
  canShare: boolean;
  rbacLoading: boolean;
  disconnecting: boolean;
  /** The collapsed connected entry for this slug in the active view. */
  entry?: AppTileEntry;
  storageScope?: StorageScope | null;
  /** Access chip render prop — popover state lives in the panel.
   *  scopeAware: false where a dedicated 📁 folders chip sits beside it. */
  renderAccessChip: (slug: string, opts?: { scopeAware?: boolean }) => React.ReactNode;
  sharePending: boolean;
  shareError?: string | null;
  onOpenScopePicker: (slug: string) => void;
  onAddAccount: (slug: string, knownIds: string[]) => void;
  onShare: (slug: string, share: boolean) => void;
  onConnect: (slug: string) => void;
  onDisconnectOrg: (slug: string) => void;
  onDisconnectPersonal: (slug: string) => void;
  /**
   * Browser-lane fallback for ASSISTED tiles (2026-08-19): a catalog app
   * with no managed OAuth, no API-key scheme, and no no-auth pass used to
   * show the same gold Connect with nowhere real to go. This hands the app
   * to the same concierge the custom row uses.
   */
  onBrowserSignIn?: (appName: string) => void;
  /**
   * This app already has a Prest0n-browser sign-in (2026-09-02). Browser-lane
   * connections are custom records keyed by NAME, not Composio connections,
   * so `connected` above (which reads the Composio roster) stays false for
   * them — the tile used to keep offering a sign-in the user had already
   * done. The connected section at the top of the page is where such a
   * connection is managed; this only stops the tile from asking again.
   */
  browserConnection?: {
    status: string;
    name?: string | null;
    slug?: string | null;
    signInUrl?: string | null;
    url?: string | null;
    scope?: string | null;
  } | null;
  /**
   * PICK THE SIGN-IN BACK UP (slice E, 2026-10-02): a tile whose sign-in stands but is not connected
   * ('registered', 'signin-lost') reopens THAT record (connectCustomFlow.resumeCustomSignIn). It used to
   * call onBrowserSignIn, which started a NEW connect for an app whose record already stood.
   */
  onResumeBrowserSignIn?: (connection: NonNullable<AppTileProps['browserConnection']>) => void;
}

export const AppTile: React.FC<AppTileProps> = ({
  toolkit: tk,
  connected,
  connecting,
  accountsLoading,
  activeView,
  canManage,
  canShare,
  rbacLoading,
  disconnecting,
  entry,
  storageScope,
  renderAccessChip,
  sharePending,
  shareError,
  onOpenScopePicker,
  onAddAccount,
  onShare,
  onConnect,
  onDisconnectOrg,
  onDisconnectPersonal,
  onBrowserSignIn,
  browserConnection,
  onResumeBrowserSignIn,
}) => {
  const slug = tk.slug.toLowerCase();
  // Connect lane — the ONE classifier every surface shares (2026-09-02).
  // Only ~122 of the catalog's ~1,448 toolkits carry Composio-managed auth;
  // this tile used to render its gold Connect button for all of them, and
  // for the other ~1,326 that button could only ever open a sign-in window
  // that vanished behind an error. The lane now decides the primary CTA:
  // 'browser' tiles lead with Prest0n's browser sign-in (the concierge that
  // was previously demoted to a small underlined afterthought).
  const lane = connectLaneFor(tk);
  const browserLane = !connected && lane === 'browser';
  // The one sign-in phase every surface reads (slice E): the browser-lane button's words follow it.
  // Keyed by the name the window reports under: a standing record's own name when it is picked back up (it
  // can be matched through the toolkit's slug, "teams" for "Microsoft Teams"), else the name a new sign-in
  // starts with.
  const signIn = useBrowserSignInPhase(browserConnection?.name || tk.name || tk.slug, browserConnection);
  // The browser-lane button can do something: start a sign-in, or pick a standing one back up.
  const canPressSignIn = Boolean(onBrowserSignIn || (browserConnection && onResumeBrowserSignIn));
  const noSignIn = !connected && lane === 'none';

  return (
    <div
      data-testid={`app-tile-${slug}`}
      style={{
        border: `1px solid ${connected ? panelTheme.successBorder : panelTheme.grayBorder}`,
        borderRadius: panelTheme.radiusCard, padding: '1rem',
        background: connected ? panelTheme.successBg : '#fff',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Shared icon element (2026-08-20): the same img-or-letter the
              onboarding surfaces use — a missing/404ing logo now shows the
              letter monogram instead of nothing. */}
          <IntegrationIcon logo={tk.meta?.logo} name={tk.name} size={28} />
          <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{tk.name}</div>
        </div>
        {!connected && (
          <span style={{
            fontSize: '0.65rem', padding: '0.15rem 0.35rem', borderRadius: '4px',
            background: panelTheme.grayBg, color: panelTheme.grayText, fontWeight: 500,
          }}>
            {/* Honest auth-method badge (2026-08-16; 2026-08-17 founder
                callout: WhatsApp said 1-Click, then demanded a WABA ID).
                Derived from the SAME lane the button routes on (2026-09-02)
                so the badge and the CTA can never disagree — the old
                'API key' badge sat above a Connect button that ran the
                OAuth mint and failed. */}
            {laneBadgeLabel(lane)}
          </span>
        )}
      </div>

      <p style={{
        fontSize: '0.8rem', color: panelTheme.grayText, marginBottom: '0.75rem', lineHeight: 1.4,
        // Real 2-line clamp (2026-08-18) — the old 100-char slice cut
        // mid-word and read as broken UI.
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {tk.meta?.description || 'Connect your account'}
      </p>

      {/* Organization chip row: folder scope, access, account count,
          shared-by-a-member note, + add account (manage-gated). */}
      {connected && activeView === 'organization' && (() => {
        const storage = isStorageToolkit(tk.slug);
        const scope = storageScope;
        const chipLabel = !scope
          ? '…'
          : scope.mode === 'folders'
            ? `📁 ${scope.folders.length} folder${scope.folders.length !== 1 ? 's' : ''}`
            : 'Everything';
        const clickable = !!scope && canManage && !rbacLoading;
        const connectionCount = entry?.connections?.length || 0;
        return (
          <div style={{ marginBottom: '0.6rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {storage && (
              <button
                onClick={() => { if (clickable) onOpenScopePicker(slug); }}
                disabled={!clickable}
                title={!scope
                  ? 'Loading folder access...'
                  : rbacLoading
                    ? 'Checking permissions...'
                    : canManage ? 'Change what Prest0n can use' : 'Ask an owner or admin'}
                style={{
                  fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                  borderRadius: panelTheme.radiusChip,
                  border: scope?.mode === 'folders' ? `1px solid ${panelTheme.gold}` : '1px solid transparent',
                  cursor: clickable ? 'pointer' : 'not-allowed',
                  background: panelTheme.goldTintStrong,
                  color: panelTheme.goldInk,
                  opacity: !scope ? 0.6 : 1,
                }}
              >
                {chipLabel}
              </button>
            )}
            {/* The 📁 chip above already tells the folder story on this row. */}
            {renderAccessChip(slug, { scopeAware: false })}
            {connectionCount > 1 && (
              <span style={{ fontSize: '0.72rem', color: panelTheme.grayText, fontWeight: 500 }}>
                {connectionCount} accounts
              </span>
            )}
            {/* Member-shared rows: attribution by display NAME only — never
                the sharer's uid (house rule); generic note when unresolved. */}
            {entry?.viaShared && (
              <span style={{ fontSize: '0.72rem', color: panelTheme.grayText, fontWeight: 500 }}>
                shared by {entry.sharedByName || 'a member'}
              </span>
            )}
            {canManage && (
              <button
                onClick={() => onAddAccount(slug, (entry?.connections || []).map((c) => c.id))}
                disabled={connecting}
                data-testid={`add-account-${slug}`}
                title="Connect another account of this app"
                style={{
                  fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                  borderRadius: panelTheme.radiusChip, border: `1px dashed ${panelTheme.gold}`,
                  background: 'none', color: panelTheme.goldInk, cursor: 'pointer',
                  opacity: connecting ? 0.6 : 1,
                }}
              >
                + add account
              </button>
            )}
          </div>
        );
      })()}

      {/* Personal chip row: access chip on every connected tile, gold
          shared-with-organization chip, share/unshare for holders of
          integrations.share, + add account (every member — ownership is
          server-enforced). */}
      {connected && activeView === 'personal' && (() => {
        const shared = !!entry?.sharedWithOrg;
        const showShareControl = canShare && !rbacLoading;
        return (
          <div style={{ marginBottom: '0.6rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {renderAccessChip(slug)}
            {shared && (
              <span style={{
                fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                borderRadius: panelTheme.radiusChip, background: panelTheme.goldTintStrong,
                color: panelTheme.goldInk,
              }}>
                shared with organization
              </span>
            )}
            {showShareControl && (
              <button
                onClick={() => { if (!sharePending) onShare(slug, !shared); }}
                disabled={sharePending}
                data-testid={`share-toggle-${slug}`}
                title={shared ? 'Stop sharing with the organization' : 'Share with the organization'}
                style={{
                  fontSize: '0.72rem', fontWeight: 600, padding: 0,
                  background: 'none', border: 'none', color: panelTheme.goldInk,
                  textDecoration: 'underline',
                  cursor: sharePending ? 'wait' : 'pointer',
                  opacity: sharePending ? 0.6 : 1,
                }}
              >
                {sharePending ? '…' : shared ? 'unshare' : 'share with organization'}
              </button>
            )}
            {/* Second accounts for EVERYONE (2026-08-18): personal tiles get
                + add account too — ownership is server-enforced, no manage
                gate on your own accounts. */}
            <button
              onClick={() => onAddAccount(slug, (entry?.connections || []).map((c) => c.id))}
              disabled={connecting}
              data-testid={`add-account-${slug}`}
              title="Connect another account of this app"
              style={{
                fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                borderRadius: panelTheme.radiusChip, border: `1px dashed ${panelTheme.gold}`,
                background: 'none', color: panelTheme.goldInk, cursor: 'pointer',
                opacity: connecting ? 0.6 : 1,
              }}
            >
              + add account
            </button>
            {(entry?.connections?.length || 0) > 1 && (
              <span style={{ fontSize: '0.72rem', color: panelTheme.grayText, fontWeight: 500 }}>
                {entry!.connections!.length} accounts
              </span>
            )}
            {shareError && (
              <span style={{ fontSize: '0.72rem', color: panelTheme.danger }}>
                {shareError}
              </span>
            )}
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: panelTheme.grayFaint }}>
          {tk.meta?.tools_count || 0} tools
        </span>
        {accountsLoading ? (
          <span style={{ fontSize: '0.75rem', color: panelTheme.grayFaint }}>Checking status...</span>
        ) : connected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', color: panelTheme.success, fontWeight: 600 }}>● Connected</span>
            {activeView === 'personal' ? (
              <button
                onClick={() => { if (!disconnecting) onDisconnectPersonal(tk.slug); }}
                disabled={disconnecting}
                data-testid={`disconnect-${slug}`}
                aria-label={disconnecting ? 'Disconnecting…' : `Disconnect ${tk.name}`}
                title={disconnecting ? 'Disconnecting…' : 'Disconnect your account'}
                style={{
                  background: 'none',
                  border: `1px solid ${panelTheme.dangerBorder}`,
                  borderRadius: '6px',
                  color: panelTheme.danger,
                  cursor: disconnecting ? 'wait' : 'pointer',
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.35rem',
                  opacity: disconnecting ? 0.5 : 1,
                }}
              >
                {disconnecting ? '…' : '✕'}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (rbacLoading || !canManage || disconnecting) return;
                  onDisconnectOrg(tk.slug);
                }}
                disabled={disconnecting || rbacLoading || !canManage}
                data-testid={`disconnect-${slug}`}
                aria-label={disconnecting ? 'Disconnecting…' : `Disconnect ${tk.name}`}
                title={disconnecting
                  ? 'Disconnecting…'
                  : rbacLoading
                    ? 'Checking permissions...'
                    : !canManage
                      ? 'You do not have permission to disconnect integrations.'
                      : 'Disconnect'}
                style={{
                  background: 'none',
                  border: (canManage && !rbacLoading) ? `1px solid ${panelTheme.dangerBorder}` : `1px solid ${panelTheme.grayBorder}`,
                  borderRadius: '6px',
                  color: (canManage && !rbacLoading) ? panelTheme.danger : panelTheme.grayFaint,
                  cursor: rbacLoading ? 'wait' : canManage ? 'pointer' : 'not-allowed',
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.35rem',
                  opacity: (disconnecting || rbacLoading || !canManage) ? 0.5 : 1,
                }}
              >
                {disconnecting ? '…' : '✕'}
              </button>
            )}
          </div>
        ) : noSignIn ? (
          // no_auth toolkits: there is nothing to sign into. Saying so beats
          // a Connect button that mints nothing (2026-09-02).
          <span style={{ fontSize: '0.78rem', color: panelTheme.grayText, fontWeight: 600 }}>
            ready to use
          </span>
        ) : browserLane && browserConnection?.status === 'connected' ? (
          // Already signed in through Prest0n's browser — manage it from the
          // connected section above, not by signing in again.
          <span style={{ fontSize: '0.85rem', color: panelTheme.success, fontWeight: 600 }}>
            ● Signed in with Prest0n
          </span>
        ) : browserLane ? (
          // The lane's REAL action, as the primary button (2026-09-02): this
          // app has no one-click sign-in, so Prest0n signs in on its own
          // computer. Same concierge the custom row uses.
          <button
            onClick={() => {
              if (signIn.inert) return;
              // A record that stands is picked back up on its own slug; only a tile with no record
              // starts a new sign-in.
              if (browserConnection && onResumeBrowserSignIn) onResumeBrowserSignIn(browserConnection);
              else onBrowserSignIn?.(tk.name || tk.slug);
            }}
            disabled={!canPressSignIn}
            data-testid={`browser-signin-${slug}`}
            data-signin-phase={signIn.phase}
            aria-disabled={signIn.inert ? 'true' : undefined}
            aria-busy={signIn.busy ? 'true' : undefined}
            title={signIn.status || laneExplainer(lane, tk.name || tk.slug)}
            style={{
              padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
              border: 'none', background: panelTheme.gold, color: panelTheme.inkSoft,
              fontWeight: 600, cursor: !canPressSignIn ? 'not-allowed' : signIn.inert ? 'default' : 'pointer',
              opacity: canPressSignIn ? 1 : 0.6,
            }}
          >
            {signIn.button}
          </button>
        ) : activeView === 'personal' ? (
          <button
            onClick={() => onConnect(tk.slug)}
            disabled={connecting}
            data-testid={`connect-${slug}`}
            style={{
              padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
              border: 'none', background: panelTheme.gold, color: panelTheme.inkSoft,
              fontWeight: 600, cursor: 'pointer',
              opacity: connecting ? 0.6 : 1,
            }}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        ) : (
          <button
            onClick={() => {
              if (rbacLoading || !canManage) return;
              onConnect(tk.slug);
            }}
            disabled={connecting || rbacLoading || !canManage}
            data-testid={`connect-${slug}`}
            title={rbacLoading
              ? 'Checking permissions...'
              : !canManage ? 'You do not have permission to connect integrations.' : undefined}
            style={{
              padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
              border: 'none',
              background: rbacLoading ? 'rgba(198, 167, 94, 0.45)' : canManage ? panelTheme.gold : panelTheme.grayFaint,
              color: canManage || rbacLoading ? panelTheme.inkSoft : '#fff',
              fontWeight: 600,
              cursor: rbacLoading ? 'wait' : canManage ? 'pointer' : 'not-allowed',
              opacity: (connecting || rbacLoading || !canManage) ? 0.6 : 1,
            }}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        )}
      </div>

      {/* Browser-lane tiles say WHY in one line (2026-09-02). The lane's
          action is the gold button above now — this used to be a small
          underlined link BESIDE a Connect button that could only fail
          (2026-08-19's rung, promoted after the owner's report). */}
      {browserLane && browserConnection?.status !== 'connected' && (
        <div
          data-testid={`browser-signin-status-${slug}`}
          style={{
            marginTop: '0.5rem', fontSize: '0.74rem', color: panelTheme.grayText, lineHeight: 1.4,
          }}
        >
          {/* Where the sign-in stands, once it has started; before that, why this lane. */}
          {signIn.status || laneExplainer(lane, tk.name || tk.slug)}
        </div>
      )}
    </div>
  );
};

export default AppTile;
